var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Label, Metric, Text } from "../../tremor/Text";
import { Icon } from "../../tremor/Icon";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { toast, Toaster } from "sonner";
import { QuestionMessage } from "../../components/QuestionMessage";
import { QuestionHistory } from "../../components/QuestionHistory";
import React from "react";
import { useBackend } from "../Wrapper";
import { useDashboard } from "../Dashboard/useDashboard";
import { ChartLoader } from "../../components/ChartLoader";
import { LogType } from "@onvo-ai/js";
import { twMerge } from "tailwind-merge";
import { WidgetLibrary } from "../../components/WidgetLibrary";
import { ChevronRightIcon, XMarkIcon } from "@heroicons/react/16/solid";
import { useTheme } from "../Dashboard/useTheme";
import { PromptInput } from "../../components/PromptInput";
import { useMaxHeight } from "../../lib/useMaxHeight";
import { Bars3Icon } from "@heroicons/react/24/outline";
dayjs.extend(relativeTime);
const CopilotRaw = ({ trigger, variant, dashboardId, coupled }) => {
    var _a, _b, _c, _d;
    const { backend, setContainerRef } = useBackend();
    const { dashboard, setId, refreshWidgets, tab: dashboardTab } = useDashboard();
    const theme = useTheme();
    const scroller = useRef(null);
    const localRef = useRef(null);
    const { lg, sm } = useMaxHeight();
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [questionLoading, setQuestionLoading] = useState(false);
    const [selectedQuestion, setSelectedQuestion] = useState();
    const [messages, setMessages] = useState([]);
    const [tab, setTab] = useState("home");
    const [userQuery, setUserQuery] = useState("");
    const [sidebar, setSidebar] = useState(false);
    useEffect(() => {
        if ((localRef === null || localRef === void 0 ? void 0 : localRef.current) && !coupled) {
            setContainerRef(localRef);
        }
    }, [localRef, coupled]);
    useEffect(() => {
        if (dashboard && dashboard.id) {
            setLoading(false);
        }
    }, [dashboard]);
    useEffect(() => {
        if (backend && (!dashboard || dashboard.id !== dashboardId) && !loading) {
            setLoading(true);
            setId(dashboardId, backend);
        }
    }, [dashboard, dashboardId, backend, loading]);
    useEffect(() => {
        if (!window.Onvo) {
            window.Onvo = {};
        }
        window.Onvo.setCopilotOpen = (val) => setOpen(val);
    }, []);
    useEffect(() => {
        if (!open) {
            setMessages([]);
            setTab("home");
            setSelectedQuestion(undefined);
            setUserQuery("");
        }
    }, [open]);
    useEffect(() => {
        if (!trigger) {
            setOpen(true);
        }
    }, [trigger]);
    useEffect(() => {
        if (selectedQuestion) {
            getMessages(selectedQuestion.id);
        }
        else {
            setMessages([]);
        }
    }, [selectedQuestion, userQuery]);
    const getMessages = (id) => __awaiter(void 0, void 0, void 0, function* () {
        let msg = yield (backend === null || backend === void 0 ? void 0 : backend.question(id).getMessages());
        if (!msg || msg.length === 0) {
            if (userQuery && userQuery.trim() !== "") {
                msg = [{
                        role: "user",
                        content: userQuery,
                        created_at: dayjs().toISOString(),
                        id: "new",
                    }];
            }
        }
        setMessages(msg && msg.length > 0 ? msg : []);
        setTimeout(scrollToBottom, 100);
    });
    const scrollToBottom = () => {
        if (scroller.current) {
            scroller.current.scrollTop = scroller.current.scrollHeight;
        }
    };
    const addToDashboard = (widget, library) => __awaiter(void 0, void 0, void 0, function* () {
        if (!backend)
            return;
        toast.promise(() => __awaiter(void 0, void 0, void 0, function* () {
            let wid = yield backend.widgets.create(Object.assign(Object.assign({}, widget), { use_in_library: library || false, use_in_chat: false, tab: dashboardTab || 0, layouts: {
                    lg: {
                        x: 0,
                        y: lg,
                        w: 4,
                        h: widget.type === "metric" ? 8 : 20,
                    },
                    sm: {
                        x: 0,
                        y: sm,
                        w: 3,
                        h: widget.type === "metric" ? 8 : 20,
                    },
                } }));
            yield backend.widget(wid.id).updateCache();
            return wid;
        }), {
            loading: library
                ? "Adding widget to library..."
                : "Adding widget to dashboard...",
            success: (widget) => {
                refreshWidgets(backend);
                setSelectedQuestion(undefined);
                setMessages([]);
                setOpen(false);
                setUserQuery("");
                if (backend) {
                    backend.logs.create({
                        type: LogType.EditWidget,
                        dashboard: widget.dashboard,
                        widget: widget.id,
                    });
                }
                if (library) {
                    return "Widget added to library";
                }
                else {
                    return "Widget added to dashboard";
                }
            },
            error: (error) => library
                ? "Error adding widget to library: " + error.message
                : "Error adding widget to dashboard: " + error.message,
        });
    });
    const createQuestion = (query) => __awaiter(void 0, void 0, void 0, function* () {
        if (!dashboard)
            return;
        setTab("chat");
        try {
            let response = yield (backend === null || backend === void 0 ? void 0 : backend.questions.create({
                dashboard: dashboard === null || dashboard === void 0 ? void 0 : dashboard.id,
                query: query,
            }));
            if (!response) {
                throw new Error("Failed to create question.");
            }
            handleAskQuestion(query, response.id);
            setUserQuery(query);
            setSelectedQuestion(response);
        }
        catch (e) {
            toast.error("Failed to create question: ", e.message);
        }
    });
    const handleAskQuestion = (query, questionId) => __awaiter(void 0, void 0, void 0, function* () {
        const qId = questionId || (selectedQuestion === null || selectedQuestion === void 0 ? void 0 : selectedQuestion.id);
        if (!qId)
            return;
        try {
            setQuestionLoading(true);
            yield (backend === null || backend === void 0 ? void 0 : backend.question(qId).completion(query, log => {
                getMessages(qId);
                setUserQuery("");
            }));
            getMessages(qId);
            setQuestionLoading(false);
        }
        catch (error) {
            console.error("Error asking question:", error);
            toast.error(error.message, {
                duration: Infinity,
                closeButton: true
            });
            setQuestionLoading(false);
        }
    });
    const handleEditMessage = (messageId, content) => __awaiter(void 0, void 0, void 0, function* () {
        if (!selectedQuestion)
            return;
        try {
            setQuestionLoading(true);
            // waitForJob
            let response = yield (backend === null || backend === void 0 ? void 0 : backend.question(selectedQuestion.id).updateMessage(messageId, content));
            getMessages(selectedQuestion.id);
            if (!response) {
                toast.error("Failed to edit message.");
                setQuestionLoading(false);
                return;
            }
            console.log("RESPONSE: ", response);
            getMessages(selectedQuestion.id);
            setQuestionLoading(false);
        }
        catch (error) {
            console.error("Error asking question:", error);
            setQuestionLoading(false);
        }
    });
    const questionMessageList = useMemo(() => {
        return messages.map((message, index) => (React.createElement(QuestionMessage, { key: message.id, questionLoading: questionLoading, message: message, isLast: index === messages.length - 1, onReply: (msg) => {
                handleAskQuestion(msg);
            }, onEdit: (msg) => {
                handleEditMessage(message.id, msg);
            }, onAdd: (widget, library) => {
                addToDashboard(widget, library);
            } })));
    }, [messages, questionLoading]);
    return (React.createElement(React.Fragment, null,
        React.createElement("dialog", { open: open },
            React.createElement("div", { ref: localRef, className: twMerge("onvo-root-style onvo-copilot-modal onvo-@container/questionmodal onvo-foreground-color onvo-animate-dialogOpen onvo-z-[50] onvo-fixed", variant === "fullscreen"
                    ? "onvo-h-full onvo-w-full onvo-left-0"
                    : "onvo-h-[calc(100vh-40px)] onvo-w-[480px] onvo-right-5 onvo-bottom-5 onvo-rounded-2xl onvo-overflow-hidden onvo-border-solid onvo-border onvo-border-slate-200 dark:onvo-border-slate-800 onvo-shadow-xl") },
                !coupled && React.createElement(Toaster, { position: "bottom-right", richColors: true }),
                React.createElement("div", { className: "onvo-question-modal-question-list onvo-flex onvo-flex-col onvo-foreground-color onvo-absolute onvo-w-full onvo-right-0 onvo-top-0 onvo-z-20 onvo-h-full" },
                    React.createElement("div", { className: "onvo-foreground-color onvo-h-12 onvo-top-0 onvo-w-full onvo-z-10 onvo-flex onvo-flex-row onvo-justify-between onvo-items-center onvo-gap-4 onvo-border-solid onvo-border-b onvo-px-3 onvo-border-black/10 onvo-py-2 dark:onvo-border-white/10" },
                        tab === "chat" ? (React.createElement(Icon, { icon: Bars3Icon, variant: "shadow", className: "onvo-border-black/10 onvo-background-color dark:onvo-border-white/10", onClick: () => {
                                setSidebar(!sidebar);
                            } })) : React.createElement("div", null),
                        React.createElement("div", { className: "onvo-flex onvo-w-full @xl/questionmodal:onvo-w-auto onvo-flex-row onvo-gap-1 onvo-justify-start onvo-items-center" },
                            React.createElement(Text, { className: "onvo-hidden @xl/questionmodal:onvo-block" }, dashboard === null || dashboard === void 0 ? void 0 : dashboard.title),
                            React.createElement(ChevronRightIcon, { className: "onvo-hidden @xl/questionmodal:onvo-block onvo-h-4 onvo-w-4 dark:onvo-fill-slate-500" }),
                            React.createElement(Label, null, ((_a = dashboard === null || dashboard === void 0 ? void 0 : dashboard.settings) === null || _a === void 0 ? void 0 : _a.copilot_title) || "Copilot")),
                        trigger ? (React.createElement(Icon, { icon: XMarkIcon, variant: "shadow", className: "onvo-border-black/10 onvo-background-color dark:onvo-border-white/10", onClick: () => {
                                setOpen(false);
                            } })) : React.createElement("div", null)),
                    React.createElement("div", { className: "onvo-flex onvo-flex-grow onvo-w-full onvo-h-[calc(100%-52px)] onvo-overflow-y-auto onvo-scrollbar-thin onvo-flex-col " },
                        ["home", "library", "history"].indexOf(tab) >= 0 && (React.createElement("div", { className: "onvo-py-2 onvo-px-2 onvo-w-full onvo-max-w-screen-lg onvo-flex onvo-mx-auto onvo-flex-col onvo-gap-4" },
                            tab === "home" && (React.createElement("div", { className: "onvo-w-full onvo-flex onvo-flex-col onvo-items-center" },
                                React.createElement(Metric, { className: "onvo-pt-10 onvo-text-center onvo-mb-4" }, ((_b = dashboard === null || dashboard === void 0 ? void 0 : dashboard.settings) === null || _b === void 0 ? void 0 : _b.copilot_description) ||
                                    "Create a widget"),
                                React.createElement(PromptInput, { hideSuggestions: true, url: (_c = dashboard === null || dashboard === void 0 ? void 0 : dashboard.settings) === null || _c === void 0 ? void 0 : _c.help_url, onSubmit: (val) => {
                                        createQuestion(val);
                                        setTab("chat");
                                    } }))),
                            (tab === "home" || tab === "library") && (React.createElement(WidgetLibrary, { onExpanded: (bool) => bool ? setTab("library") : setTab("home"), onClose: () => setOpen(false) })),
                            (tab === "home" || tab === "history") && (React.createElement(QuestionHistory, { variant: "default", onSelect: (q) => {
                                    setSelectedQuestion(q);
                                    setMessages([]);
                                    setTab("chat");
                                }, onExpanded: (bool) => bool ? setTab("history") : setTab("home"), onDelete: () => {
                                    setSelectedQuestion(undefined);
                                    setTab("home");
                                } })))),
                        tab === "chat" && (React.createElement("div", { className: "onvo-flex onvo-flex-row onvo-w-full onvo-h-full" },
                            React.createElement("div", { className: "onvo-z-50 onvo-left-0 onvo-absolute @xl/questionmodal:onvo-relative onvo-overflow-x-hidden onvo-flex-shrink-0 onvo-transition-all onvo-ease-in-out " + (sidebar ? "onvo-w-[calc(100%-16px)] @xl/questionmodal:onvo-w-[260px]" : "onvo-w-0 @xl/questionmodal:onvo-w-0") },
                                React.createElement(QuestionHistory, { variant: "sidebar", onSelect: (q) => {
                                        setSelectedQuestion(q);
                                        setMessages([]);
                                        setTab("chat");
                                    }, selectedId: selectedQuestion === null || selectedQuestion === void 0 ? void 0 : selectedQuestion.id, onNew: () => {
                                        setSelectedQuestion(undefined);
                                        setMessages([]);
                                        setTab("home");
                                    }, onExpanded: (bool) => bool ? setTab("history") : setTab("home"), onDelete: () => {
                                        setSelectedQuestion(undefined);
                                        setTab("home");
                                    } })),
                            React.createElement("div", { className: "onvo-w-full onvo-h-full onvo-flex onvo-flex-col onvo-px-2" },
                                React.createElement("div", { className: "onvo-flex onvo-w-full onvo-flex-grow onvo-flex-col onvo-gap-4 onvo-overflow-y-auto onvo-scrollbar-thin onvo-py-2", ref: scroller },
                                    React.createElement("div", { className: "onvo-flex onvo-flex-col onvo-relative onvo-mx-auto onvo-w-full onvo-max-w-screen-md" },
                                        questionMessageList,
                                        questionLoading && (React.createElement(ChartLoader, { id: selectedQuestion === null || selectedQuestion === void 0 ? void 0 : selectedQuestion.id })))),
                                React.createElement(PromptInput, { url: (_d = dashboard === null || dashboard === void 0 ? void 0 : dashboard.settings) === null || _d === void 0 ? void 0 : _d.help_url, className: "onvo-mb-4", onSubmit: val => {
                                        handleAskQuestion(val);
                                    } })))))))),
        trigger && React.cloneElement(trigger, {
            onClick: (e) => {
                setOpen(true);
            },
        })));
};
export const Copilot = memo(CopilotRaw);
//# sourceMappingURL=Copilot.js.map